import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '../../components/icons.jsx'
import { ACTIVITY_TYPE_LABELS } from '../../constants/activityTypes.js'

const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export default function ActivityTypePicker({ value, types, onChange, labels = ACTIVITY_TYPE_LABELS, label = 'Tipo de atividade', searchable = true, placeholder = 'Selecionar tipo' }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [position, setPosition] = useState({})
  const trigger = useRef(null)
  const popup = useRef(null)
  const id = useId()
  const filtered = types.filter(type => normalize(labels[type] || type).includes(normalize(query.trim())))
  const close = () => { setOpen(false); trigger.current?.focus() }
  const choose = type => { onChange(type); close() }

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const rect = trigger.current.getBoundingClientRect()
      const width = Math.min(rect.width, 420, window.innerWidth - 16)
      const height = popup.current.offsetHeight
      setPosition({ width, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), top: Math.max(8, rect.bottom + height + 6 < window.innerHeight ? rect.bottom + 6 : rect.top - height - 6) })
    }
    const outside = event => {
      if (!trigger.current?.contains(event.target) && !popup.current?.contains(event.target)) setOpen(false)
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(popup.current)
    document.addEventListener('pointerdown', outside)
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      observer.disconnect()
      document.removeEventListener('pointerdown', outside)
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  useLayoutEffect(() => {
    if (open) document.getElementById(`${id}-${active}`)?.scrollIntoView({ block: 'nearest' })
  }, [active, open, id])

  return <>
    <button ref={trigger} type="button" className="dp-type-trigger" aria-label={label} aria-haspopup="listbox" aria-expanded={open} onClick={() => { setQuery(''); setActive(0); setOpen(!open) }}>
      <span>{labels[value] || value || placeholder}</span><span className="dp-field-chevron" aria-hidden="true"><Icon n={open ? 'chevron_u' : 'chevron_d'} s={14} sw={1.7} /></span>
    </button>
    {open && createPortal(<div ref={popup} className="dp-type-popup" style={position} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget) && !trigger.current?.contains(event.relatedTarget)) setOpen(false) }} onKeyDown={event => {
      if (event.key === 'Escape') { event.stopPropagation(); event.preventDefault(); close() }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        setActive(index => Math.max(0, Math.min(filtered.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))))
      }
      if (searchable && event.key === 'Enter') { event.preventDefault(); if (filtered[active]) choose(filtered[active]) }
    }}>
      {searchable && <input autoFocus role="combobox" aria-label={`Buscar ${label.toLowerCase()}`} aria-expanded="true" aria-controls={id} aria-autocomplete="list" aria-activedescendant={filtered[active] ? `${id}-${active}` : undefined} placeholder={`Buscar ${label.toLowerCase()}…`} value={query} onChange={event => { setQuery(event.target.value); setActive(0) }} />}
      <div id={id} role="listbox" aria-label={label} className="dp-type-options">
        {filtered.map((type, index) => <button id={`${id}-${index}`} type="button" role="option" aria-selected={value === type} tabIndex={searchable ? -1 : 0} autoFocus={!searchable && index === 0} key={type} className={index === active ? 'is-active' : ''} onMouseDown={event => event.preventDefault()} onClick={() => choose(type)}>
          <span>{labels[type] || type}</span>{value === type && <span aria-hidden="true">✓</span>}
        </button>)}
      </div>
      {!filtered.length && <p>Nenhuma opção encontrada.</p>}
    </div>, document.body)}
  </>
}
